import { normalizeField, parseDate, parseBoolean, parseInteger, ensureArray } from '@/services/base/BaseNormalizer';
import { unwrapResponse } from '@/services/base/ResponseWrapper';
import { objectToCamelCase } from '@/services/base/CaseConverter';
import { validateListingResponse, validateTabCountResponse } from './validateNormalization';

const FIELD_SEQUENCE_MAP = {
  'Customer Name': 1,
  'Property Location': 2,
  'Adjuster Name': 3,
  'Policy Type': 4,
  'Policy Number': 5,
  'Loss Type': 6,
  'Loss Date': 7,
  'Reported Date': 8,
  'Claim Type': 9,
  'Carrier Contact Name': 10,
  'Carrier Contact Phone': 11,
  'Carrier Contact Email': 12,
  'Customer Phone': 13,
  'Customer Email': 14,
  'Carrier Claim Number': 15,
  'Claim Source': 16,
  'Litigation Workkard Number': 17,
  'Track': 18,
  'Building Type': 19,
  'Building Description': 20,
};

const normalizeCustomField = (raw = {}) => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  
  const fieldGroup = raw.fieldGroupName || raw.FieldGroup || '';
  const sequence = FIELD_SEQUENCE_MAP[fieldGroup] !== undefined 
    ? FIELD_SEQUENCE_MAP[fieldGroup] 
    : (raw.sequence || 999);
  
  return {
    Description: raw.description || '',
    FieldType: raw.fieldType || '',
    Group: raw.group || '',
    IsRequired: raw.isRequired || false,
    MaxLength: raw.maxLength || 0,
    FieldGroup: fieldGroup,
    Label: raw.label || '',
    Name: raw.fieldName || '',
    Sequence: sequence,
    Value: raw.value || '',
    IsNonEditable: raw.isNonEditable || false,
    PreInitScript: raw.preInitScript || '',
    PreSaveScript: raw.presaveScript || '',
    FieldKey: raw.fieldKey || '',
    CustomFieldId: raw.customFieldId || 0,
    FieldSettings: raw.settings || '',
    Options: raw.items || [],
    MapFieldKey: raw.mapFieldKey || '',
    IsSystemField: raw.isSystemField || false
  };
};

export const normalizeWorkkardListItem = (raw = {}) => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const rawCustomFields = raw.custom_fields || raw.customFields || raw.CustomFields || [];

  const camelCased = objectToCamelCase(raw);

  return {
    workkardId: normalizeField(camelCased, ['workkardId', 'id'], null),
    workkardNumber: normalizeField(camelCased, ['workkardNumber'], ''),
    templateShortName: normalizeField(camelCased, ['templateShortName'], ''),
    workkardTitle: normalizeField(camelCased, ['workkardTitle'], ''),
    workkardSummary: normalizeField(camelCased, ['workkardSummary'], ''),
    carrierName: normalizeField(camelCased, ['carrierName'], ''),
    createdDate: normalizeField(camelCased, ['createdDate'], ''),
    createdDateString: normalizeField(camelCased, ['createdDateString'], ''),
    workkardStatus: normalizeField(camelCased, ['workkardStatus'], 'Open'),
    closedDate: normalizeField(camelCased, ['closedDate', 'workkardCloseDateString'], ''),
    colorCode: normalizeField(camelCased, ['colorCode', 'templateShortName'], ''),
    workkardTemplateId: normalizeField(camelCased, ['workkardTemplateId', 'templateId'], null),
    sponsorImage: normalizeField(camelCased, ['sponsorImage', 'workkardOwnerImage', 'ownerImage'], ''),
    sponsorName: normalizeField(camelCased, ['sponsorName', 'workkardOwner', 'workkardOwnerName', 'ownerName'], ''),
    sponsorId: normalizeField(camelCased, ['sponsorId', 'sponsor', 'workkardOwnerId', 'ownerId'], null),
    workkardOwner: normalizeField(camelCased, ['workkardOwner', 'workkardOwnerName', 'sponsorName', 'ownerName'], ''),
    workkardOwnerImage: normalizeField(camelCased, ['workkardOwnerImage', 'sponsorImage', 'ownerImage'], ''),
    workkardOwnerId: normalizeField(camelCased, ['workkardOwnerId', 'sponsorId', 'ownerId'], null),
    customFields: ensureArray(rawCustomFields)
      .map(field => normalizeCustomField(objectToCamelCase(field)))
      .filter(Boolean),
  };
};

export const normalizeTabCounts = (raw = {}) => {
  if (!raw || typeof raw !== 'object') {
    return {
      openCount: 0,
      closeCount: 0,
      unassignedCount: 0,
      watchingCount: 0,
    };
  }

  const camelCased = objectToCamelCase(raw);

  return {
    openCount: parseInteger(normalizeField(camelCased, ['openCount'], 0), 0),
    closeCount: parseInteger(normalizeField(camelCased, ['closeCount'], 0), 0),
    unassignedCount: parseInteger(normalizeField(camelCased, ['unassignedCount', 'unassignCount'], 0), 0),
    watchingCount: parseInteger(normalizeField(camelCased, ['watchingCount'], 0), 0),
  };
};

export const normalizeListingResponse = (response, isScroll = false) => {
  
  if (!response) {
    return {
      workkardsFullData: [],
      total: 0,
      skip: 0,
      take: 0,
      page: 1,
      status: '',
      sortDir: 'DESC',
      sortBy: 1,
    };
  }

  const unwrapped = unwrapResponse(response);
  
  validateListingResponse(unwrapped);
  
  const listData = ensureArray(normalizeField(unwrapped, ['list', 'workkardsFullData'], []));
  
  return {
    workkardsFullData: listData.map(normalizeWorkkardListItem).filter(Boolean),
    total: parseInteger(normalizeField(unwrapped, ['total'], 0), 0),
    skip: parseInteger(normalizeField(unwrapped, ['skip'], 0), 0),
    take: parseInteger(normalizeField(unwrapped, ['take'], 0), 0),
    page: parseInteger(normalizeField(unwrapped, ['page'], 1), 1),
    status: normalizeField(unwrapped, ['status'], ''),
    sortDir: normalizeField(unwrapped, ['sortDir'], 'DESC'),
    sortBy: parseInteger(normalizeField(unwrapped, ['sortBy'], 1), 1),
  };
};

export const normalizeTabCountResponse = (response) => {
  
  if (!response) {
    return {
      tabCounts: normalizeTabCounts({}),
      sessionContext: null,
    };
  }

  const unwrapped = unwrapResponse(response);
  
  validateTabCountResponse(unwrapped);
  
  const tabCounts = normalizeTabCounts(unwrapped);


  return {
    tabCounts,
    sessionContext: normalizeField(unwrapped, ['sessionContext'], null),
  };
};
